import { isEmpty, map, mapValues, omitBy } from "lodash";
import { stringify } from "query-string";
import type { QuestionType, Translations } from "../types";
import {
  newOption,
  newProposal,
  newQuestion,
  type EditorQuestion,
} from "./editorOperations";

// The half-written composer post, carried over to the new page outside the URL
const QUESTION_KEY = "questionnaire_composer_question";
const DESCRIPTION_KEY = "questionnaire_composer_description";
const DESCRIPTION_TRANSLATIONS_KEY =
  "questionnaire_composer_description_translations";

export type ComposerQuestion = {
  type?: string;
  content?: string;
  translations?: Translations;
  options?: { label?: string; translations?: Translations }[];
  proposals?: {
    date?: string | null;
    time?: string | null;
    time_end?: string | null;
  }[];
  multiple_select?: boolean;
  option_adding?: boolean;
  add_improper_schedule_option?: boolean;
};

const TYPES: Record<string, QuestionType> = {
  question: "text",
  survey: "poll",
  schedule: "schedule",
};

export function fromComposerQuestion(
  question: ComposerQuestion,
): EditorQuestion {
  const type = TYPES[question.type || "question"] || "text";
  const base = newQuestion(type);
  const options = (question.options || [])
    .map((option) => ({
      label: option.label?.trim() || "",
      translations: option.translations || {},
    }))
    .filter((option) => option.label !== "");
  const proposals = (question.proposals || []).filter(
    (proposal) => proposal.date,
  );

  return {
    ...base,
    content: question.content?.trim() || "",
    translations: question.translations || {},
    multiple_select: !!question.multiple_select,
    option_adding: type !== "text" && !!question.option_adding,
    add_improper_option:
      type === "schedule" && !!question.add_improper_schedule_option,
    options: isEmpty(options)
      ? base.options
      : map(options, (option) => ({
          ...newOption(option.label),
          translations: option.translations,
        })),
    proposals: isEmpty(proposals)
      ? base.proposals
      : map(proposals, (proposal) =>
          newProposal(
            proposal.date || null,
            proposal.time || null,
            proposal.time_end || null,
          ),
        ),
  };
}

function store(key: string, value: string) {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    // without storage the new page starts empty
  }
}

function take(key: string): string | null {
  try {
    const value = sessionStorage.getItem(key);
    sessionStorage.removeItem(key);
    return value;
  } catch {
    return null;
  }
}

export function handOverComposerQuestion(question: ComposerQuestion) {
  store(QUESTION_KEY, JSON.stringify(question));
}

export function takeComposerQuestion(): EditorQuestion | null {
  const stored = take(QUESTION_KEY);
  try {
    return stored ? fromComposerQuestion(JSON.parse(stored)) : null;
  } catch {
    return null;
  }
}

export function takeComposerDescription(): string {
  return take(DESCRIPTION_KEY) || "";
}

// The composer keeps the post's languages under the field "content", the
// questionnaire writes the same text to its description
export function takeComposerDescriptionTranslations(): Translations {
  const stored = take(DESCRIPTION_TRANSLATIONS_KEY);
  if (!stored) return {};

  try {
    const translations: Translations = JSON.parse(stored);
    return omitBy(
      mapValues(translations, (fields) =>
        omitBy({ description: fields?.content || "" }, isEmpty),
      ),
      isEmpty,
    );
  } catch {
    return {};
  }
}

export function openQuestionnaireEditor(
  streams: { value: string }[] | undefined,
  description?: string,
  descriptionTranslations?: Translations,
) {
  window.InterceptorPrompt.allowNavigation();
  if (description) store(DESCRIPTION_KEY, description);
  if (description && !isEmpty(descriptionTranslations)) {
    store(
      DESCRIPTION_TRANSLATIONS_KEY,
      JSON.stringify(descriptionTranslations),
    );
  } else {
    // an abandoned handover must not leave its languages for the next one
    take(DESCRIPTION_TRANSLATIONS_KEY);
  }

  const query = isEmpty(streams)
    ? {}
    : { streams: map(streams, "value").join(",") };
  // A full load like the appointment form, the composer's leave prompt is lifted above
  window.location.href = `/questionnaires/new?${stringify(query)}`;
}
